import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Bank, HandCoins, Coins, Receipt, Download, Plus, Shield, CurrencyInr, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const DEPARTMENTS = ["CSC", "MahaOnline", "Aaple Sarkar", "UIDAI", "NSDL PAN", "Passport Seva", "Revenue", "Other"];
const PAY_MODES = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque"];

export default function GovFinance() {
  const [fin, setFin] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [backups, setBackups] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [settleOpen, setSettleOpen] = useState(false);
  const [commOpen, setCommOpen] = useState(false);
  const [settleForm, setSettleForm] = useState({ date: today(), department: "CSC", amount_collected: "", amount_deposited: "", reference_no: "", payment_mode: "Bank Transfer", remarks: "" });
  const [commForm, setCommForm] = useState({ date: today(), department: "CSC", amount: "", reference_no: "", status: "Pending", remarks: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [f, s, c, b] = await Promise.all([
        api.get("/finance/dashboard"),
        api.get("/settlements"),
        api.get("/commissions"),
        api.get("/system/backups").catch(() => ({ data: [] })),
      ]);
      setFin(f.data); setSettlements(s.data); setCommissions(c.data); setBackups(b.data || []);
    } catch { }
  };
  useEffect(() => { load(); }, []);

  const saveSettlement = async () => {
    if (!settleForm.amount_collected) { toast.error("Enter amount collected"); return; }
    setBusy(true);
    try {
      await api.post("/settlements", { ...settleForm, amount_collected: Number(settleForm.amount_collected), amount_deposited: Number(settleForm.amount_deposited) || 0 });
      toast.success("Settlement recorded"); setSettleOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const saveCommission = async () => {
    if (!commForm.amount) { toast.error("Enter amount"); return; }
    setBusy(true);
    try {
      await api.post("/commissions", { ...commForm, amount: Number(commForm.amount) });
      toast.success("Commission recorded"); setCommOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.get("/system/backup-encrypted", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `priyanka-backup-${new Date().toISOString().slice(0, 10)}.json.enc`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Backup downloaded (AES-256-GCM encrypted)");
      setTimeout(load, 800);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const markCommissionReceived = async (c) => {
    try {
      await api.patch(`/commissions/${c.id}`, { status: "Received" });
      toast.success("Marked as received"); await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold mb-2 flex items-center gap-2">
          <Bank size={14} weight="fill" /> Government &amp; Financial
        </div>
        <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Government Settlement &amp; Commission</h1>
        <p className="text-sm text-slate-600 mt-1.5">Separate government money from business income. Track deposits, commissions, expenses & net profit.</p>
      </div>

      {/* Master P&L cards */}
      {fin && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="finance-cards">
          <KpiCard label="Govt Fees Collected" value={inr(fin.gov_fees_collected)} tone="cyan" icon={Bank} />
          <KpiCard label="Service Charges" value={inr(fin.service_charges)} tone="indigo" icon={CurrencyInr} />
          <KpiCard label="Commission Received" value={inr(fin.commission_received)} tone="emerald" icon={HandCoins} sub={fin.commission_pending > 0 ? `${inr(fin.commission_pending)} pending` : ""} />
          <KpiCard label="Expenses" value={inr(fin.expenses)} tone="rose" icon={TrendDown} />
          <KpiCard label="Net Profit" value={inr(fin.net_profit)} tone={fin.net_profit >= 0 ? "emerald" : "rose"} icon={TrendUp} big />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="dashboard" data-testid="fin-tab-dashboard">Overview</TabsTrigger>
          <TabsTrigger value="settlements" data-testid="fin-tab-settlements">Settlements ({settlements.length})</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="fin-tab-commissions">Commissions ({commissions.length})</TabsTrigger>
          <TabsTrigger value="backups" data-testid="fin-tab-backups">Backups</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-3">
          {fin && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="p-4 bg-cyan-50 border-cyan-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-700 font-semibold mb-2">Government Money</div>
                <Row label="Collected" value={inr(fin.gov_fees_collected)} />
                <Row label="Deposited" value={inr(fin.gov_fees_deposited)} />
                <Row label="Pending" value={inr(fin.gov_fees_pending)} bold className="text-rose-700" />
              </Card>
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">Business Income</div>
                <Row label="Service Charges" value={inr(fin.service_charges)} />
                <Row label="Commissions Received" value={inr(fin.commission_received)} />
                <Row label="Total Income" value={inr(fin.service_charges + fin.commission_received)} bold />
              </Card>
              <Card className="p-4 bg-slate-50 border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold mb-2">Outflows</div>
                <Row label="Expenses" value={inr(fin.expenses)} />
                <Row label="Salary Due (this month)" value={inr(fin.salary_due)} />
                <Row label="Net Profit" value={inr(fin.net_profit)} bold className={fin.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"} />
              </Card>
            </div>
          )}
          <Card className="p-3 bg-amber-50 border-amber-200 text-xs text-amber-800">
            💡 <b>Tip:</b> "Net Profit" = Service Charges + Commissions Received − Expenses. Government fees are NOT counted as your income because they're pass-through to the department.
          </Card>
        </TabsContent>

        {/* Settlements */}
        <TabsContent value="settlements">
          <Card className="p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Deposits to Government Portals</div>
              <Button size="sm" onClick={() => setSettleOpen(true)} data-testid="fin-new-settle" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1"><Plus size={13} /> New Settlement</Button>
            </div>
            {settlements.length === 0 ? <Empty text="No settlements recorded yet." /> : (
              <SimpleTable
                headers={["Date", "Dept", "Collected", "Deposited", "Balance", "Ref", "Status"]}
                rows={settlements.map((s) => [
                  s.date, s.department, inr(s.amount_collected), inr(s.amount_deposited),
                  <span className={s.balance > 0 ? "text-rose-700 font-semibold" : ""}>{inr(s.balance)}</span>,
                  <span className="font-mono text-xs">{s.reference_no || "—"}</span>,
                  <Badge variant="outline" className={s.status === "Settled" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{s.status}</Badge>,
                ])}
              />
            )}
          </Card>
        </TabsContent>

        {/* Commissions */}
        <TabsContent value="commissions">
          <Card className="p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Commissions Received from Portals</div>
              <Button size="sm" onClick={() => setCommOpen(true)} data-testid="fin-new-comm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><Plus size={13} /> New Commission</Button>
            </div>
            {commissions.length === 0 ? <Empty text="No commissions recorded yet." /> : (
              <SimpleTable
                headers={["Date", "Dept", "Amount", "Ref", "Status", ""]}
                rows={commissions.map((c) => [
                  c.date, c.department, inr(c.amount),
                  <span className="font-mono text-xs">{c.reference_no || "—"}</span>,
                  <Badge variant="outline" className={c.status === "Received" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{c.status}</Badge>,
                  c.status !== "Received" ? <Button size="sm" variant="outline" onClick={() => markCommissionReceived(c)} data-testid={`fin-comm-mark-${c.id}`} className="h-7 text-[10px]">Mark received</Button> : null,
                ])}
              />
            )}
          </Card>
        </TabsContent>

        {/* Backups */}
        <TabsContent value="backups">
          <Card className="p-4 bg-white space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Encrypted Backups (AES-256-GCM)</div>
                <div className="text-xs text-slate-500 mt-1">Files are compressed + encrypted with the platform key. Restore requires the same key.</div>
              </div>
              <Button onClick={downloadBackup} data-testid="fin-backup-btn" className="gap-2 bg-slate-800 hover:bg-slate-900 text-white">
                <Shield size={14} weight="fill" /> Backup Now
              </Button>
            </div>
            {backups.length === 0 ? <Empty text="No backups yet. Click 'Backup Now' to create your first." /> : (
              <SimpleTable
                headers={["Filename", "Docs", "Size", "Created At", "By"]}
                rows={backups.map((b) => [
                  <span className="font-mono text-xs">{b.filename}</span>,
                  b.total_docs,
                  humanBytes(b.size_bytes),
                  <span className="text-xs">{new Date(b.created_at).toLocaleString()}</span>,
                  <span className="text-xs">{b.created_by}</span>,
                ])}
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Settlement dialog */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>New Settlement</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={settleForm.date} onChange={(e) => setSettleForm({ ...settleForm, date: e.target.value })} /></Field>
            <Field label="Department">
              <Select value={settleForm.department} onValueChange={(v) => setSettleForm({ ...settleForm, department: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount Collected"><Input type="number" value={settleForm.amount_collected} onChange={(e) => setSettleForm({ ...settleForm, amount_collected: e.target.value })} data-testid="fin-st-amt-col" /></Field>
            <Field label="Amount Deposited"><Input type="number" value={settleForm.amount_deposited} onChange={(e) => setSettleForm({ ...settleForm, amount_deposited: e.target.value })} data-testid="fin-st-amt-dep" /></Field>
            <Field label="Payment Mode">
              <Select value={settleForm.payment_mode} onValueChange={(v) => setSettleForm({ ...settleForm, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Reference No"><Input value={settleForm.reference_no} onChange={(e) => setSettleForm({ ...settleForm, reference_no: e.target.value })} placeholder="UTR / Challan" /></Field>
            <Field full label="Remarks"><Textarea rows={2} value={settleForm.remarks} onChange={(e) => setSettleForm({ ...settleForm, remarks: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
            <Button onClick={saveSettlement} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="fin-st-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Commission dialog */}
      <Dialog open={commOpen} onOpenChange={setCommOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>New Commission</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date"><Input type="date" value={commForm.date} onChange={(e) => setCommForm({ ...commForm, date: e.target.value })} /></Field>
            <Field label="Department">
              <Select value={commForm.department} onValueChange={(v) => setCommForm({ ...commForm, department: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Amount"><Input type="number" value={commForm.amount} onChange={(e) => setCommForm({ ...commForm, amount: e.target.value })} data-testid="fin-cm-amt" /></Field>
            <Field label="Status">
              <Select value={commForm.status} onValueChange={(v) => setCommForm({ ...commForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Pending", "Received"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Reference No" full><Input value={commForm.reference_no} onChange={(e) => setCommForm({ ...commForm, reference_no: e.target.value })} /></Field>
            <Field full label="Remarks"><Textarea rows={2} value={commForm.remarks} onChange={(e) => setCommForm({ ...commForm, remarks: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommOpen(false)}>Cancel</Button>
            <Button onClick={saveCommission} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="fin-cm-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function humanBytes(n) { if (!n) return "0 B"; const k = 1024, u = ["B", "KB", "MB", "GB"]; const i = Math.floor(Math.log(n) / Math.log(k)); return `${(n / Math.pow(k, i)).toFixed(1)} ${u[i]}`; }

function KpiCard({ label, value, tone, icon: Icon, sub, big }) {
  const tones = { cyan: "border-l-cyan-500", indigo: "border-l-indigo-500", emerald: "border-l-emerald-500", rose: "border-l-rose-500", amber: "border-l-amber-500" };
  return (
    <Card className={`p-3 border-l-4 ${tones[tone] || tones.indigo} bg-white`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
        {Icon && <Icon size={14} weight="fill" className="text-slate-300" />}
      </div>
      <div className={`${big ? "text-3xl" : "text-2xl"} font-bold text-slate-900 mt-0.5`} style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      {sub && <div className="text-[10px] text-amber-600 mt-0.5">{sub}</div>}
    </Card>
  );
}

function Row({ label, value, bold, className }) {
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className="text-slate-600">{label}</span>
      <span className={`${bold ? "font-bold" : "font-medium"} ${className || "text-slate-800"}`}>{value}</span>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{label}</div>
      {children}
    </div>
  );
}

function Empty({ text }) { return <div className="text-center py-8 text-sm text-slate-400">{text}</div>; }

function SimpleTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
          <tr>{headers.map((h, i) => <th key={i} className="text-left py-2 pr-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
              {r.map((c, j) => <td key={j} className="py-2 pr-3 text-slate-700">{c ?? "—"}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
